import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import type {
  ClientToServerEvents,
  ServerToClientEvents,
  GameAction,
  GameConfig,
  CreateRoomData,
  JoinRoomData
} from '@youraiquiz/shared';
import { useGameStore } from '../stores/gameStore';

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// Server URL - uses proxy in development
const SOCKET_URL = import.meta.env.PROD
  ? import.meta.env.VITE_API_URL || ''
  : '';

let socketInstance: TypedSocket | null = null;

// Helper to get fresh store actions - avoids stale closure issues in socket handlers
const getStoreActions = () => useGameStore.getState();

export const useSocket = () => {
  const socketRef = useRef<TypedSocket | null>(null);
  const clientState = useGameStore((s) => s.clientState);

  // Initialize socket connection (runs once)
  useEffect(() => {
    if (socketInstance) {
      socketRef.current = socketInstance;
      return;
    }

    const socket: TypedSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    socketInstance = socket;
    socketRef.current = socket;

    // Connection events - use getStoreActions() to always get fresh references
    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      const { setConnected, setReconnecting } = getStoreActions();
      setConnected(true);

      // Auto-reconnect to room if we have a saved session
      // This handles both: socket.io auto-reconnect (new socket ID) and page reload
      const { sessionToken, myId } = useGameStore.getState().clientState;
      const { roomCode } = useGameStore.getState().gameState;

      if (sessionToken && myId && roomCode) {
        console.log('[AUTO-RECONNECT] Saved session found, reconnecting to room', roomCode);
        setReconnecting(true);
        socket.emit('reconnect_room', {
          roomCode,
          playerId: myId,
          sessionToken
        });
      } else {
        setReconnecting(false);
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      const { setConnected, reset } = getStoreActions();
      setConnected(false);

      if (reason === 'io server disconnect') {
        // Server initiated disconnect, don't auto-reconnect
        reset();
      }
    });

    socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      getStoreActions().setError('Connection failed. Retrying...');
    });

    // Game events
    socket.on('room_created', (data) => {
      console.log('Room created:', data.roomCode);
      const { setMyId, setSessionToken, setGameState } = getStoreActions();
      setMyId(data.playerId);
      setSessionToken(data.sessionToken);
      setGameState(data.state);
    });

    socket.on('room_joined', (data) => {
      console.log('Room joined');
      const { setMyId, setSessionToken, setGameState } = getStoreActions();
      setMyId(data.playerId);
      setSessionToken(data.sessionToken);
      setGameState(data.state);
    });

    socket.on('reconnected', (data) => {
      console.log('Reconnected to room');
      const { setMyId, setGameState, setReconnecting } = getStoreActions();
      setMyId(data.playerId);
      setGameState(data.state);
      setReconnecting(false);
    });

    socket.on('game_state', (state) => {
      console.log(`[RECEIVED] Status: ${state.status}, Players: ${state.players.map((p: { name: string; hasVoted: boolean }) => `${p.name}(voted:${p.hasVoted})`).join(', ')}`);
      getStoreActions().setGameState(state);
    });

    socket.on('player_joined', (player) => {
      console.log('Player joined:', player.name);
    });

    socket.on('player_left', (data) => {
      console.log('Player left:', data.playerId);
    });

    socket.on('round_timer', (data) => {
      // Timer sync from server - handled by game state
      console.log('Round timer:', data.endTime);
    });

    socket.on('error', (data) => {
      console.error('Server error:', data.code, data.message);
      const { setError, setConnected, reset } = getStoreActions();

      // Handle room-related errors by clearing session and showing friendly message
      if (data.code === 'RECONNECT_FAILED' || data.code === 'ROOM_NOT_FOUND') {
        sessionStorage.removeItem('youraiquiz-session');
        // Reset state but keep connection
        const wasConnected = useGameStore.getState().clientState.isConnected;
        reset();
        if (wasConnected) {
          setConnected(true);
        }
        setError('Your previous room has ended. Start a new game!');
        // Auto-clear the message after 4 seconds
        setTimeout(() => getStoreActions().setError(null), 4000);
      } else {
        setError(data.message);
      }
    });

    // Reconnection events
    socket.io.on('reconnect_attempt', (attempt) => {
      console.log('Reconnection attempt:', attempt);
      getStoreActions().setReconnecting(true);
    });

    socket.io.on('reconnect', (attempt) => {
      console.log('Reconnected after', attempt, 'attempts');
      getStoreActions().setReconnecting(false);
    });

    socket.io.on('reconnect_failed', () => {
      console.error('Reconnection failed');
      const { setError, setReconnecting } = getStoreActions();
      setError('Failed to reconnect. Please refresh the page.');
      setReconnecting(false);
    });
  }, []);

  // Separate effect for polling and visibility sync — always runs, even on remount
  useEffect(() => {
    const socket = socketInstance;
    if (!socket) return;
    socketRef.current = socket;

    // Handle visibility change - sync state when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && socket.connected) {
        const { sessionToken, myId } = useGameStore.getState().clientState;
        const { roomCode } = useGameStore.getState().gameState;

        if (sessionToken && myId && roomCode) {
          console.log('[VISIBILITY] Tab visible, requesting state sync');
          socket.emit('request_state', (response) => {
            if (response?.state) {
              getStoreActions().setGameState(response.state);
            }
          });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic state sync: catch missed broadcasts by polling every 2s during active games
    const syncInterval = setInterval(() => {
      if (!socket.connected) return;
      const { roomCode, status } = useGameStore.getState().gameState;
      if (!roomCode || status === 'LANDING') return;

      socket.emit('request_state', (response) => {
        if (response?.state) {
          const currentStatus = useGameStore.getState().gameState.status;
          if (currentStatus !== response.state.status) {
            console.log(`[SYNC] Status drift detected: ${currentStatus} -> ${response.state.status}`);
            getStoreActions().setGameState(response.state);
          }
        }
      });
    }, 2000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(syncInterval);
    };
  }, []);

  // Create a new room
  const createRoom = useCallback((name: string, penalty: string) => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      getStoreActions().setError('Not connected to server');
      return;
    }

    const data: CreateRoomData = {
      player: { name, penalty }
    };

    socket.emit('create_room', data);
  }, []);

  // Join an existing room
  const joinRoom = useCallback((roomCode: string, name: string, penalty: string) => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      getStoreActions().setError('Not connected to server');
      return;
    }

    const data: JoinRoomData = {
      roomCode: roomCode.toUpperCase(),
      player: { name, penalty }
    };

    socket.emit('join_room', data);
  }, []);

  // Send a game action (with ack callback for reliable state delivery)
  const sendAction = useCallback((action: GameAction) => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      getStoreActions().setError('Not connected to server');
      return;
    }

    socket.emit('game_action', { action }, (response) => {
      if (response?.state) {
        console.log(`[ACK] State from ack: ${response.state.status}`);
        getStoreActions().setGameState(response.state);
      }
    });
  }, []);

  // Disconnect and reset
  const disconnect = useCallback(() => {
    const socket = socketRef.current;
    if (socket) {
      socket.disconnect();
      socketInstance = null;
      socketRef.current = null;
    }
    getStoreActions().reset();
  }, []);

  // Clear session without disconnecting socket - preserve connection state
  const clearSession = useCallback(() => {
    sessionStorage.removeItem('youraiquiz-session');
    // Reset game state but preserve connection
    const { reset, setConnected } = getStoreActions();
    const wasConnected = useGameStore.getState().clientState.isConnected;
    reset();
    if (wasConnected) {
      setConnected(true);
    }
  }, []);

  // Manually trigger reconnection to saved room
  const reconnectToRoom = useCallback(() => {
    const socket = socketRef.current;
    if (!socket?.connected) {
      getStoreActions().setError('Not connected to server');
      return;
    }

    const { sessionToken, myId } = useGameStore.getState().clientState;
    const { roomCode } = useGameStore.getState().gameState;

    if (sessionToken && myId && roomCode) {
      getStoreActions().setReconnecting(true);
      socket.emit('reconnect_room', {
        roomCode,
        playerId: myId,
        sessionToken
      });
    } else {
      getStoreActions().setError('No saved session found');
    }
  }, []);

  // Leave room and return to landing
  const leaveRoom = useCallback(() => {
    const socket = socketRef.current;
    if (socket?.connected) {
      socket.emit('leave_room');
    }
    // Clear session using fresh store reference
    sessionStorage.removeItem('youraiquiz-session');
    const { reset, setConnected } = getStoreActions();
    const wasConnected = useGameStore.getState().clientState.isConnected;
    reset();
    if (wasConnected) {
      setConnected(true);
    }
  }, []);

  return {
    isConnected: clientState.isConnected,
    isReconnecting: clientState.isReconnecting,
    createRoom,
    joinRoom,
    sendAction,
    disconnect,
    clearSession,
    reconnectToRoom,
    leaveRoom
  };
};

// Hook for dispatching actions
export const useGameActions = () => {
  const { sendAction } = useSocket();
  const isHost = useGameStore((s) => s.gameState.hostId === s.clientState.myId);

  return {
    // Host actions
    setConfig: (config: Partial<GameConfig>) => {
      if (isHost) sendAction({ type: 'SET_CONFIG', payload: config });
    },
    kickPlayer: (playerId: string) => {
      if (isHost) sendAction({ type: 'KICK_PLAYER', payload: { playerId } });
    },
    startGame: () => {
      if (isHost) sendAction({ type: 'START_GAME' });
    },
    startGenerating: () => {
      if (isHost) sendAction({ type: 'START_GENERATING' });
    },
    startQuiz: () => {
      if (isHost) sendAction({ type: 'START_QUIZ' });
    },
    nextQuestion: () => {
      if (isHost) sendAction({ type: 'NEXT_QUESTION' });
    },
    goToPenalty: () => {
      if (isHost) sendAction({ type: 'GO_TO_PENALTY' });
    },
    startPenaltyRoulette: () => {
      if (isHost) sendAction({ type: 'START_PENALTY_ROULETTE' });
    },

    // Player actions
    submitCategories: (categories: string[]) => {
      sendAction({ type: 'SUBMIT_CATEGORIES', payload: { categories } });
    },
    voteCategory: (categoryId: string) => {
      sendAction({ type: 'VOTE_CATEGORY', payload: { categoryId } });
    },
    removeVote: (categoryId: string) => {
      sendAction({ type: 'REMOVE_VOTE_CATEGORY', payload: { categoryId } });
    },
    confirmVotes: () => {
      sendAction({ type: 'CONFIRM_VOTES' });
    },
    answer: (optionIndex: number) => {
      sendAction({ type: 'ANSWER', payload: { optionIndex } });
    },
    vetoPenalty: (penaltyText: string) => {
      sendAction({ type: 'VETO_PENALTY', payload: { penaltyText } });
    },
    playAgain: () => {
      sendAction({ type: 'PLAY_AGAIN' });
    }
  };
};
